import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useApi } from '../lib/apiClient';
import { useAuth } from '../lib/auth';
import { CreateReportResponseData } from '../types/reports';
import {
    Card,
    Button,
    Skeleton,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@pagespeedify/ui";
import {
    CheckCircle2 as CheckCircle2Icon,
    AlertTriangleIcon,
    XCircleIcon,
    EyeIcon,
    RefreshCwIcon,
    Share2Icon, // Added for Share button
    CopyIcon, // Added for Copy button
    Loader2Icon // Added for loading state
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner'; // Added for toast notifications

// Helper to ensure rating is valid
const validRatings = ['good', 'needs improvement', 'poor'] as const;
function getSafeRating(rating: any): 'good' | 'needs improvement' | 'poor' {
    return validRatings.includes(rating) ? rating : 'good';
}

interface ReportsHistoryProps {
    // Props can be added here if needed, e.g., initial filters, items per page
    manifestOwnerId?: string; // Made optional since we calculate it internally
}

const getRatingClasses = (rating: 'good' | 'needs improvement' | 'poor'): { icon: React.ElementType, color: string, bgColorClass?: string } => {
    // Ensure CheckCircle2Icon is correctly referenced (it might be CheckCircle2 from @pagespeedify/ui)
    const GoodIcon = CheckCircle2Icon;

    switch (rating) {
        case 'good':
            return { icon: GoodIcon, color: 'text-green-700', bgColorClass: 'bg-green-100' };
        case 'needs improvement':
            return { icon: AlertTriangleIcon, color: 'text-orange-700', bgColorClass: 'bg-orange-100' };
        case 'poor':
            return { icon: XCircleIcon, color: 'text-red-700', bgColorClass: 'bg-red-100' };
        default:
            return { icon: AlertTriangleIcon, color: 'text-gray-700', bgColorClass: 'bg-gray-100' };
    }
};

const getScoreColor = (score: number): string => {
    if (score >= 90) return 'text-green-600';
    if (score >= 50) return 'text-orange-600';
    return 'text-red-600';
};

export function ReportsHistory({ }: ReportsHistoryProps) {
    const apiClient = useApi();
    const { user } = useAuth();
    const internalUserId = user?.internalUserId;
    const navigate = useNavigate();
    // Pagination and filter state
    const ALL_DOMAINS_VALUE = "__ALL_DOMAINS__"; // Define a constant for the "All Domains" value
    const [selectedDomain, setSelectedDomain] = useState<string>(ALL_DOMAINS_VALUE); // Initialize with "All Domains"
    const [page, setPage] = useState(1);
    const REPORTS_PER_PAGE = 5; // Ensure this is set to 5
    const [sharingReportId, setSharingReportId] = useState<string | null>(null); // For loading state of individual share buttons
    const [lastCopiedUrl, setLastCopiedUrl] = useState<string | null>(null); // Store the last copied URL
    const [isGenerating, setIsGenerating] = useState(false); // Loading state for regenerate button

    // Check if the user is part of a team and if they are the team owner
    const isInTeam = !!user && !!user.team_id;
    const isTeamOwner = !!user && user.team_id && user.team_owner_id === user.internalUserId;

    // Determine the manifestOwnerId based on the user's team status
    const effectiveManifestOwnerId = isInTeam && !isTeamOwner && user ? user.team_owner_id : internalUserId;

    // Logging
    console.log('[ReportsHistory] manifestOwnerId:', effectiveManifestOwnerId);

    const {
        data: reportsArray,
        isLoading,
        isError,
        error,
        isFetching,
    } = useQuery<any[], Error>({
        queryKey: ['ReportsHistory', effectiveManifestOwnerId],
        queryFn: () => {
            if (!effectiveManifestOwnerId) {
                return Promise.reject(new Error("Internal user ID is not available."));
            }
            return apiClient.get<any[]>(
                `/reports?internalUserId=${effectiveManifestOwnerId}`
            );
        },
        enabled: !!effectiveManifestOwnerId,
        placeholderData: (previousData) => previousData,
        staleTime: 5 * 60 * 1000,
    });


    // Map manifest entries to the UI's expected ReportListItem structure
    const reports = useMemo(() => (Array.isArray(reportsArray) ? reportsArray.map((entry, idx) => {
        if (!entry || typeof entry !== 'object') {
            console.warn('Malformed manifest entry at index', idx, entry);
            return null;
        }
        const id = entry.reportId || entry.id || entry.key || entry.filename || `report-${idx}`;
        const page_url_for_display = entry.url || entry.website_url || entry.page_url || 'Unknown';
        // Ensure domain stripping is robust
        let domain = page_url_for_display.replace(/^https?:\/\/(www\.)?/, '').split('/')[0];
        let created_at = entry.generatedAt || entry.requestedAt || entry.created_at || entry.timestamp || '';
        if (created_at) {
            try {
                const dateObj = new Date(created_at);
                if (!isNaN(dateObj.getTime())) {
                    const year = dateObj.getFullYear();
                    const month = (dateObj.getMonth() + 1).toString().padStart(2, '0');
                    const day = dateObj.getDate().toString().padStart(2, '0');
                    const hours = dateObj.getHours().toString().padStart(2, '0');
                    const minutes = dateObj.getMinutes().toString().padStart(2, '0');
                    created_at = `${month}/${day}/${year} ${hours}:${minutes}`;
                } else {
                    // If date is invalid, keep original or set to a placeholder
                    console.warn('Invalid date encountered in ReportsHistory:', entry.created_at);
                    // created_at remains original or you can set 'Invalid Date'
                }
            } catch (e) {
                console.error('Error formatting date in ReportsHistory:', e);
                // created_at remains original
            }
        }
        let performanceScore = entry.performanceScore || entry.performance_score;
        if (typeof performanceScore !== 'number') performanceScore = '-';
        const strategy = entry.strategy || 'mobile'; // Extract strategy
        let metrics = { lcp: { value: '-', rating: 'good' }, inp: { value: '-', rating: 'good' }, cls: { value: '-', rating: 'good' } };
        if (entry.metrics && typeof entry.metrics === 'object') {
            metrics = {
                lcp: entry.metrics.lcp || { value: '-', rating: 'good' },
                inp: entry.metrics.inp || { value: '-', rating: 'good' },
                cls: entry.metrics.cls || { value: '-', rating: 'good' },
            };
        }
        metrics = {
            lcp: { ...metrics.lcp, rating: getSafeRating(metrics.lcp.rating) },
            inp: { ...metrics.inp, rating: getSafeRating(metrics.inp.rating) },
            cls: { ...metrics.cls, rating: getSafeRating(metrics.cls.rating) },
        };
        // Add createdById and createdByEmail to the returned object
        return {
            id,
            website_url: page_url_for_display, // Use the consistent variable
            domain,
            page_url: page_url_for_display, // Use the consistent variable
            created_at,
            performanceScore,
            metrics,
            strategy, // Add strategy to returned object
            tasks_count: entry.tasks_count || 0,
            ai_solutions_count: entry.ai_solutions_count || 0,
            createdById: entry.createdById,
            createdByEmail: entry.createdByEmail,
        };
    }).filter((r): r is NonNullable<typeof r> => !!r) : []), [reportsArray]);

    // Filtered and paginated reports
    const filteredReports = useMemo(() => {
        let filtered = reports;

        // Logging user/team info
        console.log('[ReportsHistory] user:', user);
        console.log('[ReportsHistory] isInTeam:', user?.team_id);
        console.log('[ReportsHistory] isTeamOwner:', user?.team_owner_id === user?.internalUserId);
        console.log('[ReportsHistory] effectiveManifestOwnerId:', effectiveManifestOwnerId);

        // Only filter for team members (not team owners, not normal users)
        // Team owners should see ALL reports in the manifest
        if (
            user?.team_id &&
            user.team_owner_id &&
            user.team_owner_id !== user.internalUserId // This is a team MEMBER, not the owner
        ) {
            // Team members only see reports they created
            filtered = filtered.filter(r => r.createdById === user.internalUserId);
            console.log('[ReportsHistory] Team member filtering: Only own reports', filtered);
        } else {
            // Team owners and regular users see all reports in their manifest
            console.log('[ReportsHistory] Team owner or regular user: showing all reports', filtered);
        }

        if (selectedDomain && selectedDomain !== ALL_DOMAINS_VALUE) {
            filtered = filtered.filter(r => r.domain === selectedDomain);
            console.log('[ReportsHistory] After domain filter:', filtered);
        }

        return filtered;
    }, [reports, selectedDomain, user, effectiveManifestOwnerId]);

    // Unique domains for filter dropdown
    const uniqueDomains = useMemo(() => {
        const set = new Set<string>();
        // For team owners, use all reports; for team members, use their filtered reports
        const reportsForDomains = (user?.team_id && user.team_owner_id !== user.internalUserId) 
            ? filteredReports  // Team members: only their own reports
            : reports;         // Team owners and regular users: all reports
        
        reportsForDomains.forEach(r => {
            if (r.domain && r.domain.trim() !== "") {
                set.add(r.domain);
            }
        });
        return Array.from(set);
    }, [reports, filteredReports, user]);
    const totalPages = Math.ceil(filteredReports.length / REPORTS_PER_PAGE) || 1;
    const paginatedReports = filteredReports.slice((page - 1) * REPORTS_PER_PAGE, page * REPORTS_PER_PAGE);

    // Reset page if filter changes
    React.useEffect(() => { setPage(1); }, [selectedDomain]);

    const handleViewReport = (reportId: string) => {
        console.log('Attempting to view report with ID:', reportId);
        if (!reportId) {
            console.error('Report ID is undefined or null. Cannot navigate.');
            alert('Error: Report ID is missing, cannot navigate to view the report.');
            return;
        }
        console.log(`Navigating to: /reports/${reportId}`);
        navigate(`/reports/${reportId}`);
    };

    const renderSkeletons = (count: number) => (
        Array.from({ length: count }).map((_, index) => (
            <Card key={index} className="mb-4 p-4">
                <div className="grid grid-cols-1 md:grid-cols-[2fr,1fr,1.5fr,1fr,1.5fr] gap-4 items-center">
                    <div className="md:col-span-1">
                        <Skeleton className="h-5 w-3/4 mb-1" />
                        <Skeleton className="h-4 w-1/2" />
                    </div>
                    <div><Skeleton className="h-8 w-8 rounded-full mx-auto" /></div>
                    <div className="grid grid-cols-3 gap-1">
                        <Skeleton className="h-5 w-full" />
                        <Skeleton className="h-5 w-full" />
                        <Skeleton className="h-5 w-full" />
                    </div>
                    <div className="flex flex-col md:flex-row justify-around items-center">
                        <Skeleton className="h-5 w-6 mb-1 md:mb-0" />
                        <Skeleton className="h-5 w-6" />
                    </div>
                    <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2 justify-end">
                        <Skeleton className="h-8 w-20" />
                        <Skeleton className="h-8 w-20" />
                        <Skeleton className="h-8 w-20" />
                    </div>
                </div>
            </Card>
        ))
    );

    if (isLoading && !internalUserId) {
        return (
            <div className="mt-6">
                <div className="mb-4 flex flex-wrap justify-between items-center gap-4">
                    <h2 className="text-xl font-semibold">Report History</h2>
                </div>
                <p className="text-center text-gray-500">Loading user information...</p>
            </div>
        );
    }

    if (isLoading && !reportsArray) {
        return (
            <div className="mt-6">
                <div className="mb-4 flex flex-wrap justify-between items-center gap-4">
                    <h2 className="text-xl font-semibold">Report History</h2>
                </div>
                {renderSkeletons(3)}
            </div>
        );
    }

    if (isError) {
        return <p className="text-red-500 mt-6">Error loading report history: {error?.message || 'Unknown error'}</p>;
    }

    return (
        <div className="mt-8">
            <div className="mb-4 flex flex-wrap justify-between items-center gap-4">
                <h2 className="text-xl font-semibold text-foreground">Report History</h2> {/* Ensure heading uses text-foreground */}
                <div className="flex items-center gap-4">
                    {uniqueDomains.length > 0 && ( // Show filter if there's at least one domain, or always show if you want "All Domains" even with no reports
                        <Select value={selectedDomain} onValueChange={setSelectedDomain}>
                            <SelectTrigger className="w-[180px] bg-background text-foreground border border-input">
                                <SelectValue placeholder="Filter by domain" className="text-foreground" />
                            </SelectTrigger>
                            <SelectContent className="bg-popover text-popover-foreground border border-border">
                                <SelectItem value={ALL_DOMAINS_VALUE}>All Domains</SelectItem>
                                {uniqueDomains.map(domain => (
                                    <SelectItem key={domain} value={domain}>{domain}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    )}
                </div>
            </div>
            {paginatedReports.length > 0 ? (
                <div className="space-y-2" data-tour="reports-list"> {/* Reduced space-y from 4 to 2 */}
                    <div className="hidden md:grid md:grid-cols-[2fr,1fr,1.5fr,1.5fr] gap-4 items-center px-4 py-2 text-xs font-medium text-gray-500 border-b">
                        <div className="md:col-span-1">WEBSITE & DATE</div>
                        <div className="text-center">SCORE</div>
                        <div className="text-center">METRICS (LCP/INP/CLS)</div>
                        <div className="text-right">ACTIONS</div>
                    </div>
                    {paginatedReports.map(report => {
                        return (
                            <Card key={report.id} className="p-2"> {/* Reduced p-4 to p-2 and removed hover effects */}
                                <div className="grid grid-cols-1 md:grid-cols-[2fr,1fr,1.5fr,1.5fr] gap-x-2 gap-y-2 items-center"> {/* Reduced gaps and removed TASKS/AI column */}
                                    <div className="md:col-span-1">
                                        <div className="font-medium text-foreground">{report.domain}</div>
                                        <div className="text-xs text-muted-foreground">{report.created_at}</div>
                                        {/* Attribution: only show if not created by the effective manifest owner */}
                                        {report.createdById !== effectiveManifestOwnerId && report.createdByEmail && (
                                            <div className="text-xs text-blue-700">Created by: {report.createdByEmail}</div>
                                        )}
                                    </div>
                                    <div className={`text-xl font-bold text-center ${getScoreColor(report.performanceScore as number)}`}> {/* Reduced score font size */}
                                        {report.performanceScore}
                                    </div>
                                    <div className="grid grid-cols-3 gap-1 text-xs text-center">
                                        {(['lcp', 'inp', 'cls'] as const).map(metricKey => {
                                            const metric = report.metrics[metricKey];
                                            if (!metric) return <div key={metricKey} className="p-1">-</div>;
                                            const { icon: Icon, color: metricColor } = getRatingClasses(getSafeRating(metric.rating));
                                            const ratingText = metric.rating.charAt(0).toUpperCase() + metric.rating.slice(1).replace(" needs", " Needs"); // Capitalize and format
                                            return (
                                                <div key={metricKey} className={`flex flex-col items-center p-1 rounded`}>
                                                    <Icon className={`w-4 h-4 ${metricColor}`} />
                                                    <span className={`mt-0.5 ${metricColor}`}>{metric.value}</span>
                                                    <span className={`text-xs ${metricColor} text-center`}>{ratingText}</span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                    <div className="flex flex-col sm:flex-row space-y-1 sm:space-y-0 sm:space-x-1 justify-end items-center"> {/* Reduced button spacing */}
                                        <Button 
                                            variant="outline" 
                                            size="sm" 
                                            disabled={isGenerating}
                                            onClick={async () => {
                                                if (!report.id) {
                                                    toast.error("Report ID is missing.");
                                                    return;
                                                }
                                                
                                                setIsGenerating(true);
                                                try {
                                                    // First, get the existing report to extract URL and device
                                                    const existingReport = await apiClient.get(`/reports/${report.id}`) as any;
                                                    const originalUrl = existingReport.metadata?.urlAnalyzed || existingReport.pagespeedData?.lighthouseResult?.finalUrl;
                                                    const originalStrategy = existingReport.metadata?.strategy || 'mobile';
                                                    
                                                    if (!originalUrl) {
                                                        toast.error("Could not extract URL from existing report");
                                                        return;
                                                    }
                                                    
                                                    // Now create a new report with the same URL and strategy
                                                    const response = await apiClient.post<CreateReportResponseData>('/reports', {
                                                        website_url: originalUrl,
                                                        page_url: originalUrl,
                                                        strategy: originalStrategy
                                                    });
                                                    
                                                    if (response) {
                                                        const reportId = response.reportId || response.report?.id;
                                                        if (reportId) {
                                                            // Navigate to reports page with the generating report ID in state
                                                            navigate('/reports', { state: { generatingReportId: reportId } });
                                                        } else {
                                                            toast.error("Could not get new report ID");
                                                        }
                                                    }
                                                } catch (error: any) {
                                                    console.error('Error regenerating report:', error);
                                                    toast.error("Failed to regenerate report", {
                                                        description: error.message || "Please try again later"
                                                    });
                                                } finally {
                                                    setIsGenerating(false);
                                                }
                                            }} 
                                            className="w-full sm:w-auto text-xs bg-background text-foreground hover:bg-accent hover:text-accent-foreground border-input"
                                        >
                                            <RefreshCwIcon className="w-3.5 h-3.5 mr-1" /> {isGenerating ? 'Regenerating...' : 'Regen'}
                                        </Button>
                                        {/* Share Button */}
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={async () => {
                                                if (!report.id) {
                                                    toast.error("Report ID is missing.");
                                                    return;
                                                }
                                                // Prevent sharing if not the creator (only for team members, not owners)
                                                if (isInTeam && !isTeamOwner && report.createdById !== user?.internalUserId) {
                                                    toast.error("You can only share reports that you created.");
                                                    return;
                                                }

                                                setSharingReportId(report.id);
                                                setLastCopiedUrl(null);
                                                try {
                                                    const response = await apiClient.post<{ publicUrl: string }>(`/reports/${report.id}/share`, {});
                                                    if (response.publicUrl) {
                                                        navigator.clipboard.writeText(response.publicUrl);
                                                        setLastCopiedUrl(response.publicUrl);
                                                        toast.success("Shareable link copied!", {
                                                            description: response.publicUrl,
                                                            action: {
                                                                label: "Open",
                                                                onClick: () => window.open(response.publicUrl, '_blank'),
                                                            },
                                                        });
                                                    } else {
                                                        toast.error("Failed to get shareable link URL.");
                                                    }
                                                } catch (err: any) {
                                                    const errorMsg = err.response?.data?.error || err.message || "Error sharing report.";
                                                    toast.error("Sharing failed", { description: errorMsg });
                                                } finally {
                                                    setSharingReportId(null);
                                                }
                                            }}
                                            disabled={sharingReportId === report.id || (isInTeam && !isTeamOwner && report.createdById !== user?.internalUserId)}
                                            className="w-full sm:w-auto text-xs bg-background text-foreground hover:bg-accent hover:text-accent-foreground border-input"
                                            title={isInTeam && !isTeamOwner && report.createdById !== user?.internalUserId ? "You can only share your own reports" : "Share this report"}
                                        >
                                            {sharingReportId === report.id ? (
                                                <Loader2Icon className="w-3.5 h-3.5 mr-1 animate-spin" />
                                            ) : lastCopiedUrl && sharingReportId !== report.id ? ( // Show copy if a link was just made for *any* report, ideally this would be specific
                                                <CopyIcon className="w-3.5 h-3.5 mr-1" />
                                            ) : (
                                                <Share2Icon className="w-3.5 h-3.5 mr-1" />
                                            )}
                                            {sharingReportId === report.id ? "Sharing..." : "Share"}
                                        </Button>
                                        <Button variant="default" size="sm" onClick={() => handleViewReport(report.id)} className="w-full sm:w-auto text-xs">
                                            <EyeIcon className="w-3.5 h-3.5 mr-1" /> View
                                        </Button>
                                    </div>
                                </div>
                            </Card>
                        );
                    })}
                    {/* Pagination controls */}
                    {totalPages > 1 && (
                        <div className="flex justify-center items-center gap-2 mt-6 pt-4 border-t border-border">
                            <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="bg-background text-foreground hover:bg-accent hover:text-accent-foreground border-input">
                                Previous
                            </Button>
                            <span className="text-sm text-muted-foreground">
                                Page {page} of {totalPages}
                            </span>
                            <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="bg-background text-foreground hover:bg-accent hover:text-accent-foreground border-input">
                                Next
                            </Button>
                        </div>
                    )}
                </div>
            ) : (
                <Card className="p-6 text-center text-gray-500">
                    No reports found for your account.
                </Card>
            )}
        </div>
    );
}